import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { paymentsApi } from "../services/api/paymentsApi";
import { notifyCartUpdate } from "../utils/cartUpdateUtils";
import { toast } from "react-toastify";

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        toast.error("Invalid payment session");
        navigate("/");
        return;
      }
 

      try {
        // Verify the checkout session and complete the order
        // This is a backup for when webhooks don't fire (e.g., local development)
        const response: any = await paymentsApi.verifyCheckoutSession(sessionId);
        
        if (response.success && response.data) {
          setOrderDetails(response.data);
          toast.success("Payment verified successfully!");
          notifyCartUpdate()
        } else {
          console.warn("Payment verification returned unexpected response");
          // Still show success page even if verification response is unexpected
        }
      } catch (error: any) {
        console.error("Error verifying payment:", error);
        
        // Don't show error toast for timeouts - the payment was likely successful
        // Webhooks or the user can check their order history
        if (error.code !== 'ECONNABORTED') {
          toast.warning("Payment was successful, but verification is pending. Check your email or order history.");
        } else {
          toast.info("Payment processed! Verification is taking longer than expected. Check your email for confirmation.");
        }
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, navigate]);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Navigation */}
      <div className="collection_details_templet_banner d-sm-block d-none">
        <div className="page_navegation_area">
          <div className="container">
            <div className="page_navegation font_two">
              <p>
                <a href="/">Home</a> /{" "}
                <span className="active fw_700">Payment Success</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Section */}
      <section className="payment_success_section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="success_card" style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '50px 30px',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                margin: '50px 0'
              }}>
                {/* Success Icon */}
                <div className="success_icon" style={{ marginBottom: '30px' }}>
                  <svg
                    width="100"
                    height="100"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ margin: '0 auto' }}
                  >
                    <circle cx="50" cy="50" r="50" fill="#10B981" fillOpacity="0.1" />
                    <circle cx="50" cy="50" r="40" fill="#10B981" />
                    <path
                      d="M35 50L45 60L65 40"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                {/* Success Message */}
                <h1 style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '15px'
                }}>
                  Payment Successful! 🎉
                </h1>
                
                <p style={{
                  fontSize: '18px',
                  color: '#666',
                  marginBottom: '15px',
                  lineHeight: '1.6'
                }}>
                  Thank you for your purchase! Your payment has been processed successfully.
                </p>

                {orderDetails && (
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '30px'
                  }}>
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                      <strong>Order Number:</strong> {orderDetails.orderNumber}
                    </p>
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                      <strong>Total Amount:</strong> ${orderDetails.total?.toFixed(2)}
                    </p>
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                      <strong>Items:</strong> {orderDetails.items?.length || 0}
                    </p>
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                      <strong>Status:</strong>{' '}
                      <span style={{ 
                        color: orderDetails.status === 'completed' ? '#10B981' : 
                               orderDetails.status === 'processing' ? '#3b82f6' : '#666',
                        fontWeight: '600'
                      }}>
                        {orderDetails.status?.charAt(0).toUpperCase() + orderDetails.status?.slice(1)}
                      </span>
                    </p>
                  </div>
                )}

                {/* Email Notice */}
                <div style={{
                  background: '#f0fdf4',
                  border: '2px solid #10B981',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '30px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'start', gap: '15px' }}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0, marginTop: '2px' }}
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <div style={{ textAlign: 'left', flex: 1 }}>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#047857',
                        marginBottom: '8px'
                      }}>
                        Check Your Email
                      </h4>
                      <p style={{
                        fontSize: '14px',
                        color: '#065f46',
                        margin: 0,
                        lineHeight: '1.5'
                      }}>
                        We've sent your purchased digital artworks and invoice to your email address.
                        The images are attached as high-resolution files ready for download.
                      </p>
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '30px',
                  textAlign: 'left'
                }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1a1a1a',
                    marginBottom: '15px'
                  }}>
                    What's Next?
                  </h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    <li style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '10px',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        background: '#10B981',
                        color: 'white',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>
                        ✓
                      </span>
                      <span style={{ color: '#666', fontSize: '14px' }}>
                        Check your email inbox for your digital artworks
                      </span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '10px',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        background: '#10B981',
                        color: 'white',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>
                        ✓
                      </span>
                      <span style={{ color: '#666', fontSize: '14px' }}>
                        Download your high-resolution images from the attachments
                      </span>
                    </li>
                    <li style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '10px'
                    }}>
                      <span style={{
                        background: '#10B981',
                        color: 'white',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        flexShrink: 0,
                        marginTop: '2px'
                      }}>
                        ✓
                      </span>
                      <span style={{ color: '#666', fontSize: '14px' }}>
                        Keep the invoice for your records
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <a
                    href="/previous-purchase"
                    className="blue_btn"
                    style={{
                      textDecoration: 'none',
                      padding: '12px 30px',
                      fontSize: '16px',
                      fontWeight: '500',
                      borderRadius: '6px'
                    }}
                  >
                    View My Purchases
                  </a>
                  <a
                    href="/products?collection=Canvas"
                    style={{
                      textDecoration: 'none',
                      padding: '12px 30px',
                      fontSize: '16px',
                      fontWeight: '500',
                      borderRadius: '6px',
                      background: 'white',
                      border: '2px solid #3b82f6',
                      color: '#3b82f6',
                      display: 'inline-block'
                    }}
                  >
                    Continue Shopping
                  </a>
                </div>

                {/* Support Info */}
                <p style={{
                  fontSize: '14px',
                  color: '#999',
                  marginTop: '30px',
                  marginBottom: 0
                }}>
                  Need help? Contact us at{" "}
                  <a
                    href="mailto:support@digitalartstore.com"
                    style={{ color: '#3b82f6', textDecoration: 'none' }}
                  >
                    support@digitalartstore.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PaymentSuccess;

